import React, { useState, useEffect } from 'react';
import { FiMapPin, FiPhone, FiMail, FiCheckCircle, FiXCircle, FiEye, FiUsers, FiRefreshCw, FiPlus } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const RestaurantsList = () => {
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchRestaurants();
  }, []);

  const fetchRestaurants = async () => {
    try {
      const response = await api.get('/vendor-owner/restaurants');
      if (response.data.success) {
        setRestaurants(response.data.data);
      }
    } catch (error) {
      toast.error('Failed to fetch restaurants');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin h-12 w-12 border-4 border-indigo-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Restaurants</h1>
          <p className="text-gray-500 text-sm">Manage all your restaurants</p>
        </div>

        <div className="flex gap-3">
          <button
            onClick={fetchRestaurants}
            className="flex items-center gap-2 px-4 py-2 bg-white border rounded-lg shadow-sm hover:shadow-md"
          >
            <FiRefreshCw /> Refresh
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <StatCard title="Total" value={restaurants.length} />
        <StatCard title="Active" value={restaurants.filter(r => r.isActive).length} />
        <StatCard title="Inactive" value={restaurants.filter(r => !r.isActive).length} />
        <StatCard title="Pending" value={restaurants.filter(r => r.approvalStatus === 'pending').length} />
      </div>

      {/* Empty */}
      {restaurants.length === 0 ? (
        <div className="bg-white p-10 rounded-xl text-center shadow-sm">
          <h3 className="text-lg font-semibold">No Restaurants</h3>
          <p className="text-gray-500 mb-4">Start by adding your first restaurant</p>
          <button
            onClick={() => navigate('/vendor-owner/add-restaurant')}
            className="px-5 py-2 bg-indigo-600 text-white rounded-lg"
          >
            Add Restaurant
          </button>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {restaurants.map((r) => (
            <div
              key={r._id}
              className="bg-white rounded-2xl shadow-sm hover:shadow-lg transition p-5 flex flex-col justify-between"
            >
              {/* Top */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <div className="h-12 w-12 bg-indigo-100 rounded-lg flex items-center justify-center">
                    {r.documents?.restaurantLogo ? (
                      <img src={r.documents.restaurantLogo} className="h-10 w-10 rounded" />
                    ) : (
                      <FiMapPin className="text-indigo-600" />
                    )}
                  </div>

                  <div className="flex gap-2">
                    <StatusBadge active={r.isActive} />
                    <ApprovalBadge status={r.approvalStatus} />
                  </div>
                </div>

                <h3 className="font-semibold text-lg text-gray-900 mb-1">
                  {r.restaurantDetails.restaurantName}
                </h3>

                <p className="text-sm text-gray-500 mb-4 flex items-center gap-1">
                  <FiMapPin /> {r.restaurantDetails.city}
                </p>

                <div className="space-y-2 text-sm text-gray-600">
                  <p className="flex items-center gap-2"><FiPhone /> {r.personalDetails.contactNumber}</p>
                  <p className="flex items-center gap-2 truncate"><FiMail /> {r.personalDetails.email}</p>
                  <p className="flex items-center gap-2">@{r.ownerCredentials.username}</p>
                </div>
              </div>

              {/* Actions */}
              <div className="mt-5 flex gap-2">
                <ActionBtn icon={<FiEye />} label="Menu" onClick={() => navigate(`/vendor-owner/restaurant/${r._id}/menu`)} />
                <ActionBtn icon={<FiUsers />} label="Captains" onClick={() => navigate(`/vendor-owner/restaurant/${r._id}/captains`)} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Small Components
const StatCard = ({ title, value }) => (
  <div className="bg-white p-4 rounded-xl shadow-sm">
    <p className="text-xs text-gray-500">{title}</p>
    <h2 className="text-xl font-bold text-gray-900">{value}</h2>
  </div>
);

const StatusBadge = ({ active }) => (
  <span className={`px-2 py-1 text-xs rounded-full font-medium ${active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
    {active ? 'Active' : 'Inactive'}
  </span>
);

const ApprovalBadge = ({ status }) => {
  const styles = {
    approved: 'bg-green-100 text-green-700',
    pending: 'bg-yellow-100 text-yellow-700',
    rejected: 'bg-red-100 text-red-700'
  };

  return (
    <span className={`px-2 py-1 text-xs rounded-full ${styles[status]}`}>
      {status}
    </span>
  );
};

const ActionBtn = ({ icon, label, onClick }) => (
  <button
    onClick={onClick}
    className="flex-1 flex items-center justify-center gap-1 px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg"
  >
    {icon} {label}
  </button>
);

export default RestaurantsList;
